import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { HEAD_ACTION } from '@/components/admin/sessions/export-menu';
import { SessionInterruptDialog } from '@/components/admin/sessions/session-interrupt-dialog';
import { useAdminSession } from '@/lib/admin-session';
import { REVIEW_ENTRY_REASON, reviewEntryState } from '@/lib/review-entry';
import { SESSION_DETAIL_KEY, interruptSession } from '@/lib/session-api';
import {
  INTERRUPT_LINK_LABEL,
  type InterruptEntry,
  PROD_INTERRUPT_LEAD,
  PROD_INTERRUPT_LINK_LABEL,
  interruptEntry,
  interruptionRefusal,
} from '@/lib/session-interruption';
import { useInstanceContext } from '@/lib/use-instance';
import type { SessionRow } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';

/**
 * « Relecture » - the entry to the review of a session, on the head of the
 * parcours AND of the conversation (relecture.md §3.1, §3.2, issue #187;
 * maquette `retouches.html`, sections 1 and 2).
 *
 * ## Three shapes, decided by the ROW alone
 *
 * - a frozen experience with no protocol page: NOTHING is drawn - there is
 *   nothing to range, and a button that always refused would only teach people
 *   to stop reading the head;
 * - a session in progress: a package `Button` that is really `disabled`,
 *   with its sentence written under it - never `aria-disabled`
 *   on a live button, the régime of the trashed « Exporter en »;
 * - a finished, closed, interrupted or expired one: a link. A session in the
 *   trash keeps its link, since its review stays readable there (§3.4) and the
 *   page itself refuses the gestures.
 *
 * ## The sentence carries the gesture of the interruption (issue #199)
 *
 * « La relecture s'ouvre quand la session est finie. Interrompre la session »:
 * the cause and the remedy on one line (interruption.md D8). The link exists
 * only where the sentence does, never in the trash, and on the atelier never
 * on a session of the prod - which is interrupted where it is played, so the
 * sentence says so and leads there (§5). It opens `SessionInterruptDialog`,
 * and the same component serves the parcours AND the conversation: one
 * gesture, two mountings. After the gesture the screen STAYS and reads again;
 * `Relecture` turns into a link by itself, off the new status.
 *
 * ## A typed link
 *
 * The page is the sister route of the conversation (`sessions.$sessionId_.relecture.tsx`),
 * so the button is the router's own `Link`: a real `href`, the middle click, « open in
 * a new tab », and a left click that does not reload the SPA.
 */

export interface ReviewEntryButtonProps {
  /** The whole row: the box of the interruption reads the position and the clock off it. */
  readonly session: SessionRow;
}

export function ReviewEntryButton({ session }: ReviewEntryButtonProps) {
  const reasonId = useId();
  const state = reviewEntryState(session);
  const { prodOrigin } = useInstanceContext();
  if (state === 'absent') return null;

  if (state === 'disabled') {
    return (
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Button
          type="button"
          variant="accent"
          size="sm"
          compact={true}
          disabled
          aria-describedby={reasonId}
          data-testid={SESSIONS_TEST_IDS.reviewButton}
          data-state="disabled"
        >
          Relecture
        </Button>
        <ReviewEntryReason
          session={session}
          reasonId={reasonId}
          entry={interruptEntry(session, prodOrigin)}
        />
      </div>
    );
  }

  return (
    <Link
      to="/admin/sessions/$sessionId/relecture"
      params={{ sessionId: session.id }}
      data-testid={SESSIONS_TEST_IDS.reviewButton}
      data-state="open"
      className={HEAD_ACTION}
    >
      Relecture
    </Link>
  );
}

/** The sentence under the disabled button: narrow, flush right, as the maquette draws it. */
const REASON_SHAPE = 'max-w-[30ch] text-right text-gray-600 text-caption';

/** The link of the interruption, in the voice of the list's « Supprimer »: a quiet underline. */
const LINK_SHAPE =
  'cursor-pointer font-semibold text-bleu-nuit underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-2';

/**
 * The reason, and its tail: nothing, the link of the interruption, or the way
 * to the prod (`interruptEntry`). The hooks of the gesture live in the child
 * that draws the link, so a row that offers none never needs a query client.
 */
function ReviewEntryReason({
  session,
  reasonId,
  entry,
}: {
  session: SessionRow;
  reasonId: string;
  entry: InterruptEntry;
}) {
  if (entry.kind === 'link') {
    return <ReasonWithInterruption session={session} reasonId={reasonId} />;
  }
  return (
    <p className={REASON_SHAPE}>
      <span id={reasonId} data-testid={SESSIONS_TEST_IDS.reviewButtonReason}>
        {REVIEW_ENTRY_REASON}
      </span>
      {entry.kind === 'prod' ? (
        <>
          {' '}
          <span data-testid={SESSIONS_TEST_IDS.interruptProdSentence}>
            {PROD_INTERRUPT_LEAD}
            {entry.href === null ? '.' : ' :'}
          </span>
          {entry.href === null ? null : (
            <>
              {' '}
              <a
                href={entry.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={SESSIONS_TEST_IDS.interruptProdLink}
                className={LINK_SHAPE}
              >
                {PROD_INTERRUPT_LINK_LABEL}
              </a>
            </>
          )}
        </>
      ) : null}
    </p>
  );
}

/**
 * The sentence with its link, the box it opens, and the call it makes.
 *
 * ## The screen stays, and reads again - but only once the box is left
 *
 * A success closes the box and reads the detail and the list again: the stamps
 * are the server's, so nothing is patched into the cache. A REFUSAL keeps the
 * box open with its sentence, and the reading waits for the box to be closed:
 * `session-over` means the session is no longer in progress, so the reading
 * would swap this sentence for a link and unmount the box over the very
 * refusal that explains it.
 */
function ReasonWithInterruption({ session, reasonId }: { session: SessionRow; reasonId: string }) {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const reread = (): void => {
    void queryClient.invalidateQueries({ queryKey: SESSION_DETAIL_KEY(email, session.id) });
    void queryClient.invalidateQueries({ queryKey: ['sessions', email] });
  };

  const interrupt = useMutation({
    mutationFn: () => interruptSession(session.id),
    onSuccess: () => {
      setOpen(false);
      reread();
    },
  });

  const close = (): void => {
    const refused = interrupt.isError;
    setOpen(false);
    interrupt.reset();
    if (refused) reread();
  };

  return (
    <>
      <p className={REASON_SHAPE}>
        <span id={reasonId} data-testid={SESSIONS_TEST_IDS.reviewButtonReason}>
          {REVIEW_ENTRY_REASON}
        </span>{' '}
        <button
          type="button"
          data-testid={SESSIONS_TEST_IDS.interruptLink}
          onClick={() => {
            interrupt.reset();
            setOpen(true);
          }}
          className={LINK_SHAPE}
        >
          {INTERRUPT_LINK_LABEL}
        </button>
      </p>
      {open ? (
        <SessionInterruptDialog
          row={session}
          busy={interrupt.isPending}
          refusal={interrupt.isError ? interruptionRefusal(interrupt.error) : null}
          onConfirm={() => interrupt.mutate()}
          onCancel={close}
        />
      ) : null}
    </>
  );
}
